import React from 'react';
import { Checkbox, Modal, Space, Typography } from 'antd';
import { UserOutlined } from '@ant-design/icons';

const { Text } = Typography;

// LocalStorage key for "don't show again" preference
const SKIP_CUSTOMER_EXISTS_WARNING_KEY = 'skipCustomerExistsWarning';

interface CustomerExistsModalProps {
  customer: {
    id: number;
    firstname?: string;
    lastname?: string;
    email?: string;
    phone?: string;
    locale?: string;
    gender?: string;
  };
  onConfirm: () => void;
  onCancel: () => void;
  onAfterClose: () => void;
  intl: any;
}

/**
 * Shows a modal to confirm using an existing customer when contact data matches
 */
export const showCustomerExistsModal = ({
  customer,
  onConfirm,
  onCancel,
  onAfterClose,
  intl
}: CustomerExistsModalProps) => {
  let dontShowAgain = false;

  // Build customer display name
  const customerName = [customer.firstname, customer.lastname]
    .filter(Boolean)
    .join(' ') || intl.formatMessage({
      id: 'pages.lead.customer_exists_modal.unnamed_customer',
      defaultMessage: 'Unnamed Customer',
    });

  Modal.confirm({
    title: <div
      style={{
        padding: '6px',
        backgroundColor: '#f5f5f5',
        borderRadius: '4px',
        marginBottom: '16px',
      }}
    >
      <Space direction="vertical" size="small" style={{ width: '100%' }}>
        <Space>
          <UserOutlined style={{ color: '#1890ff' }} />
          <Text strong>{customerName}</Text>
        </Space>
      </Space>
    </div>,
    width: 520,
    content: (
      <div>
        {/* Explanation Text */}
        <p>
          {intl.formatMessage({
            id: 'pages.lead.customer_exists_modal.content',
            defaultMessage: 'A customer with this email or phone number already exists in the system. Would you like to use this existing customer or change the contact data?',
          })}
        </p>

        {/* Don't Show Again Checkbox */}
        <Checkbox
          onChange={(e) => {
            dontShowAgain = e.target.checked;
          }}
        >
          {intl.formatMessage({
            id: 'common.dont_show_again',
            defaultMessage: "Don't show this message again",
          })}
        </Checkbox>
      </div>
    ),
    okText: 'Ok',
    cancelText: intl.formatMessage({
      id: 'pages.lead.customer_exists_modal.change_contact',
      defaultMessage: 'Change Contact Data',
    }),
    onOk: () => {
      // Save preference if checkbox was checked
      if (dontShowAgain) {
        localStorage.setItem(SKIP_CUSTOMER_EXISTS_WARNING_KEY, 'true');
      }
      onConfirm();
    },
    onCancel: () => {
      // Save preference if checkbox was checked
      if (dontShowAgain) {
        localStorage.setItem(SKIP_CUSTOMER_EXISTS_WARNING_KEY, 'true');
      }
      onCancel();
    },
    afterClose: onAfterClose,
  });
};

// Export the localStorage key for use in other components
export { SKIP_CUSTOMER_EXISTS_WARNING_KEY };
